import { DomainEntityWithRights, EntityWithActions, Identifier } from "@/types/domain";
import { ActionResource } from "@/types/rights";
import { AssetEntity, AssetId, Content } from "@/types/squidex";
import { EnvironmentStages, getSquidexUrl } from "@lib/environment";
import { rightsActions } from "@lib/rights";

export type BackendImageTransformOptions = {
  width?: number;
  height?: number;
  mode?: string;
  cache?: number;
  auto?: boolean;
};

const SQUIDEX_IMAGE_HOSTS = new Set(
  EnvironmentStages.map((stage) => new URL(getSquidexUrl(stage)).hostname),
);
const BACKEND_IMAGE_CACHE_SECONDS = 3600;
const IMAGE_FILE_TYPES = new Set(["avif", "gif", "jpeg", "jpg", "png", "webp"]);

const isImageAsset = (asset?: AssetEntity) => {
  const mimeType = asset?.mimeType?.toLowerCase();

  if (mimeType?.startsWith("image/")) {
    return true;
  }

  const fileType = asset?.fileType?.toLowerCase().replace(/^\./, "");

  if (fileType && IMAGE_FILE_TYPES.has(fileType)) {
    return true;
  }

  return /\.(avif|gif|jpe?g|png|webp)(?:[?#]|$)/i.test(
    asset?.fileName ?? asset?.links?.content ?? "",
  );
};

const getOptimizedBackendImageUrl = (
  image?: string,
  imageTransform?: BackendImageTransformOptions,
) => {
  if (!image || !imageTransform) {
    return image;
  }

  try {
    const url = new URL(image);

    if (!SQUIDEX_IMAGE_HOSTS.has(url.hostname)) {
      return image;
    }

    url.searchParams.set("cache", (imageTransform.cache ?? BACKEND_IMAGE_CACHE_SECONDS).toString());

    if (imageTransform.auto !== false) {
      url.searchParams.set("auto", "true");
    }
    if (imageTransform.width) {
      url.searchParams.set("width", imageTransform.width.toString());
    }
    if (imageTransform.height) {
      url.searchParams.set("height", imageTransform.height.toString());
    }
    if (imageTransform.mode) {
      url.searchParams.set("mode", imageTransform.mode);
    }

    return url.toString();
  } catch {
    return image;
  }
};

const getOptimizedAssetContentUrl = (
  asset?: AssetEntity,
  imageTransform?: BackendImageTransformOptions,
) => {
  if (!asset?.links?.content || !isImageAsset(asset)) {
    return asset?.links?.content;
  }

  return getOptimizedBackendImageUrl(asset.links.content, imageTransform);
};

const getAssetWithOptimizedImageLink = (
  asset: AssetEntity,
  imageTransform?: BackendImageTransformOptions,
): AssetEntity => {
  const optimizedContent = getOptimizedAssetContentUrl(asset, imageTransform);

  if (!imageTransform || !optimizedContent || optimizedContent === asset.links?.content) {
    return asset;
  }

  return {
    ...asset,
    links: {
      ...asset.links,
      content: optimizedContent,
    },
  };
};

/**
 * A comparator function for sorting objects containing an `id` property. Supports both numeric
 * and string `id` values. Optionally allows sorting in ascending or descending order.
 *
 * @param {Object} [a] - The first object to compare, which must contain an `id` property.
 * @param {Object} [b] - The second object to compare, which must contain an `id` property.
 * @param {"asc"|"desc"} [order="asc"] - The sort order. Defaults to ascending ("asc"). Use "desc" for descending.
 * @returns {number} A negative number if the first argument should come before the second, a positive number if the first
 * argument should come after the second, or 0 if they are considered equal.
 */
const sortById = (a?: { id: Identifier }, b?: { id: Identifier }, order?: "asc" | "desc"): number =>
  sortIds(a?.id, b?.id, order);

/**
 * A function for sorting two identifiers in ascending or descending order.
 *
 * @param {Identifier} [a] - The first identifier to compare, which can be a string or a number.
 * @param {Identifier} [b] - The second identifier to compare, which can be a string or a number.
 * @param {"asc" | "desc"} [order="asc"] - The order in which to sort the identifiers.
 *                                         Defaults to "asc" for ascending order. Use "desc" for descending order.
 * @returns {number} A negative value if `a` should come before `b`, a positive value if `a` should come after `b`,
 *                   or `0` if `a` and `b` are considered equal.
 */
const sortIds = (a?: Identifier, b?: Identifier, order?: "asc" | "desc") => {
  if (!a || !b) {
    return 0;
  }

  if (typeof a === "number" && typeof b === "number")
    return order === "desc" ? (b ?? 0) - (a ?? 0) : (a ?? 0) - (b ?? 0);
  else if (typeof a === "string" && typeof b === "string")
    return order === "desc" ? ("" + b).localeCompare(a) : ("" + a).localeCompare(b);

  return 0;
};

/**
 * Adds rights to a given entity by mapping the allowed actions based on the provided resource.
 *
 * @param {EntityWithActions} entity - The entity that includes a set of available actions.
 * @param {ActionResource} resource - The resource which determines the applicable rights for the entity's actions.
 * @returns {TOut} A new entity with extended rights applied based on the provided resource.
 *
 * @template TOut Extends the DomainEntityWithRights type with no default rights.
 */
const addRightsToEntity = <TOut extends DomainEntityWithRights<never>>(
  entity: EntityWithActions,
  resource: ActionResource,
): TOut => ({
  ...rightsActions.parseActions<EntityWithActions, never, TOut>(entity, (actions, fns) => ({
    ...fns.canEntityRights(actions, resource),
  })),
});

export const domainTools = {
  sortIds,
  sortById,
  sortByIdDesc: (a?: { id: Identifier }, b?: { id: Identifier }): number => sortById(a, b, "desc"),
  sortUsedById: (
    a?: Partial<{ objectId?: Identifier }>,
    b?: Partial<{
      objectId?: Identifier;
    }>,
    order?: "asc" | "desc",
  ): number => domainTools.sortIds(a?.objectId, b?.objectId, order ?? "desc"),
  sortByNumber: (a?: number | null, b?: number | null, order?: "asc" | "desc"): number => {
    if (!a && !b) {
      return 0;
    }

    if (!a) {
      return order === "asc" ? -1 : 1;
    }

    if (!b) {
      return order === "asc" ? 1 : -1;
    }

    return order === "desc" ? b - a : a - b;
  },
  addRightsToEntity,
  messengerActions: {
    makeTelegramLinkFromString: (twitter?: string) => `https://t.me/${twitter}`,
    makeTwitterLinkFromString: (twitter?: string) => `https://twitter.com/${twitter}`,
    getFieldValueWithoutSymbols: (v: string, symbols: string | RegExp) =>
      v?.replaceAll(symbols, ""),
  },
  extractFilesFromInvariant: <T>(
    content?: Content<T>,
    field?: string,
    imageTransform?: BackendImageTransformOptions,
  ): AssetEntity[] => {
    if (!content?.data || !field) {
      return [];
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const ids = content.data[field] as AssetId;
    const assets = content?.assets;

    if (!ids?.iv || !assets || ids?.iv?.length === 0 || assets?.length === 0) {
      return [];
    }

    return (
      content.assets
        ?.filter((a) => a.id && ids.iv!.includes(a.id))
        .sort((a, b) =>
          a.createdAt && b.createdAt && new Date(a.createdAt) > new Date(b.createdAt) ? -1 : 1,
        )
        .map((asset) => getAssetWithOptimizedImageLink(asset, imageTransform)) ?? []
    );
  },
  imageActions: {
    getOptimizedBackendImageUrl,
    getOptimizedAssetContentUrl,
  },
};
